import { QuickstartCard } from './QuickstartCard';
import { ThemeLogo } from './ThemeLogo';
import {
  QuickstartTheme,
  isGameReleased,
  getWishlistButtonImage,
  getResponsiveTextSize,
  getResponsiveSpacing,
  getResolvedCtaUrl,
} from '../types/theme';
import { useIsLandscape, useForcePortrait } from '../utils/responsiveHooks';

interface QuickstartActionPageProps {
  theme: QuickstartTheme;
  headerText?: string[]; // Optional array of header text lines (e.g., ["SNEAK PEEK", "IS AT CAPACITY"])
  onButtonClick: () => void;
}

/**
 * Shared component for CTA and Error pages
 * Both pages have the same structure: logo, optional header text, game logo, release date, button
 */
export const QuickstartActionPage = ({ theme, headerText, onButtonClick }: QuickstartActionPageProps) => {
  const isLandscape = useIsLandscape();
  const { shouldRotate } = useForcePortrait();

  return (
    <div
      className='flex items-center justify-center overflow-auto'
      style={
        shouldRotate ? {
          // Landscape mode: force portrait by rotating -90deg
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          transformOrigin: 'center center',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        } : {
          // Portrait mode: normal layout
          minHeight: '100vh',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: isLandscape ? '0.25rem 1rem' : '1rem',
        }
      }
    >
      <QuickstartCard theme={theme} showHeader={false} hideFooter={true}>
        <div className='flex flex-col items-center text-center'>
          {/* Studio Logo */}
          <ThemeLogo
            kind='studio'
            theme={theme}
            isLandscape={isLandscape}
            className={getResponsiveSpacing('mt', 4, isLandscape)}
          />

          {/* Optional header text (e.g., "SNEAK PEEK", "IS AT CAPACITY") */}
          {headerText && headerText.length > 0 && (
            <div className={getResponsiveSpacing('mt', 8, isLandscape)}>
              {headerText.map((text, index) => (
                <div
                  key={index}
                  className={`font-["Inter"] ${getResponsiveTextSize('2xl', isLandscape)} font-semibold`}
                  style={{ color: '#FEF3C7' }}
                >
                  {text}
                </div>
              ))}
            </div>
          )}

          {/* Game Logo */}
          <ThemeLogo
            kind='game'
            theme={theme}
            isLandscape={isLandscape}
            className={headerText ? getResponsiveSpacing('mt', 8, isLandscape) : getResponsiveSpacing('mt', 12, isLandscape)}
            loading='eager'
          />

          {/* Release Date - only show for unreleased games */}
          {theme.releaseDate && !isGameReleased(theme) && (
            <div
              className={`${getResponsiveSpacing('mt', 6, isLandscape)} font-["Inter"] ${getResponsiveTextSize('2xl', isLandscape)} font-semibold`}
              style={{ color: theme.releaseDateTextColor || theme.primaryColor }}
            >
              <div>COMING:</div>
              <div>{theme.releaseDate}</div>
            </div>
          )}

          {/* Wishlist/Store Button */}
          {getResolvedCtaUrl(theme) && (() => {
            const buttonImage = getWishlistButtonImage(theme);
            return (
              <img
                src={buttonImage.src}
                alt={buttonImage.alt}
                onClick={onButtonClick}
                className={`${isGameReleased(theme) ? getResponsiveSpacing('mt', 8, isLandscape) : getResponsiveSpacing('mt', 3, isLandscape)} cursor-pointer hover:opacity-90`}
                style={{ height: isLandscape ? '48px' : '64px' }}
              />
            );
          })()}

          {/* Powered by Playcast */}
          <ThemeLogo
            kind='poweredBy'
            theme={theme}
            isLandscape={isLandscape}
            className={`${getResponsiveSpacing('mb', 4, isLandscape)} ${getResponsiveSpacing('mt', 12, isLandscape)}`}
          />
        </div>
      </QuickstartCard>
    </div>
  );
};

